import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SiteFooter from '../components/Footer';
import API_BASE_URL, { buildApiHeaders } from '../utils/config';
import zxcvbn from 'zxcvbn';
import '../App.css';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: { suggestions: [] as string[], warning: '' } });
  const navigate = useNavigate();

  const checkUsername = useCallback(async (username: string) => {
    if (username.length < 3 || username.length > 12) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    try {
      const res = await fetch(`${API_BASE_URL}/check_username/${username.toLowerCase()}/neutralfit`, {
        headers: buildApiHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.is_taken) {
          setUsernameStatus('available');
        } else {
          setUsernameStatus('taken');
        }
      } else {
        setUsernameStatus('idle');
      }
    } catch (err) {
      setUsernameStatus('idle');
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (formData.username) {
        checkUsername(formData.username);
      } else {
        setUsernameStatus('idle');
      }
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [formData.username, checkUsername]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'username') {
      setUsernameStatus('checking');
    }

    if (name === 'password') {
      const strength = zxcvbn(value);
      setPasswordStrength({ score: strength.score, feedback: strength.feedback });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.first_name || !formData.last_name || !formData.username || !formData.email || !formData.password) {
      setError('All fields are required.');
      return;
    }
    if (usernameStatus !== 'available') {
      setError('Please choose an available username.');
      return;
    }
    if (passwordStrength.score < 1) {
      setError('Please choose a stronger password.');
      return;
    }
    setLoading(true);
    try {
      const user = {
        id: 0, // This will be set by the backend
        first_name: formData.first_name,
        last_name: formData.last_name,
        username: formData.username,
        email: formData.email,
        password: formData.password,
        org: 'neutralfit',
      };
      const res = await fetch(`${API_BASE_URL}/create_inactive_user`, {
        method: 'POST',
        headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(user),
      });
      if (res.ok) {
        const data = await res.json();
        setError(data.message || 'Sign up successful! Please check your email to activate your account.');
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Sign up failed.');
      }
    } catch (err) {
      setError('Sign up failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nf-form-container">
      <button className="nf-home-btn" type="button" onClick={() => navigate('/')} aria-label="Close">&#10005;</button>
      <h2 className="nf-form-title">Sign Up</h2>
      <form className="nf-form nf-form-small" onSubmit={handleSubmit}>
        <div className="nf-form-row">
          <input className="nf-input" name="first_name" placeholder="First Name*" value={formData.first_name} onChange={handleChange} required />
          <input className="nf-input" name="last_name" placeholder="Last Name*" value={formData.last_name} onChange={handleChange} required />
        </div>
        <div className="nf-input-container">
          <input
            className="nf-input"
            name="username"
            placeholder="Username*"
            value={formData.username}
            onChange={handleChange}
            required
            autoComplete="off"
            maxLength={12}
          />
          <div className="nf-input-status">
            {usernameStatus === 'checking' && <span className="nf-spinner-small"></span>}
            {usernameStatus === 'available' && <span className="nf-check-mark">&#10004;</span>}
          </div>
        </div>
        {usernameStatus === 'taken' && <div className="nf-error-small">Username is taken</div>}
        {usernameStatus === 'invalid' && <div className="nf-error-small">Username must be 3-12 characters</div>}
        <input
          className="nf-input"
          name="email"
          type="email"
          placeholder="Email*"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="username"
        />
        <div className="nf-password-input-container">
          <input
            className="nf-input"
            name="password"
            type="password"
            placeholder="Password*"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
          />
          {formData.password && (
            <div className="nf-password-strength-container">
              <div className="nf-password-strength-bar-container">
                <div
                  className="nf-password-strength-bar"
                  style={{
                    width: `${(passwordStrength.score + 1) * 20}%`,
                    backgroundColor: ['#d9534f', '#f0ad4e', '#f0ad4e', '#5cb85c', '#5cb85c'][passwordStrength.score],
                  }}
                ></div>
              </div>
              <div className="nf-password-feedback">
                {passwordStrength.feedback.warning && <p className="nf-error-small">{passwordStrength.feedback.warning}</p>}
                {passwordStrength.feedback.suggestions.map((suggestion, index) => (
                  <p key={index} className="nf-info-small">{suggestion}</p>
                ))}
              </div>
            </div>
          )}
        </div>
        {formData.password.length > 0 && passwordStrength.score < 1 && (
          <div className="nf-error-small">Password is too weak.</div>
        )}
        <button className="nf-btn nf-btn-primary nf-form-btn" type="submit" disabled={loading || usernameStatus !== 'available' || passwordStrength.score < 1}>
          {loading ? 'Signing Up...' : 'Sign Up'}
        </button>
      </form>
      {error && <div className="nf-error">{error}</div>}
      <div className="nf-info-section">
        <strong>Note:</strong> <Link to="/login">Login</Link> if you already have an account. <br /> <br />
        <strong>Privacy Notice:</strong> NeutralFit does <u>not</u> collect, sell, or share your data. We will never send you marketing emails or share your information with third parties.
      </div>
      <div className="nf-info-section">
        <h3>Why do we need this info?</h3>
        <p>
          We ask for your information to make sure you're a real person and not an internet bot trying to misuse our platform. This helps us keep NeutralFit safe and secure for everyone.
        </p>
      </div>
      <SiteFooter style={{ marginTop: '30px' }} />
    </div>
  );
};

export default Signup;
